import type {
  DailyStaffingDayView,
  WeekBudgetView,
} from '../../../../services/LaborManagementService/types/laborManagementTypes';

/**
 * Builds a naive `YYYY-MM-DD` string from local calendar parts.
 *
 * Never `toISOString()`: that renders the UTC date, which names tomorrow from about 7 p.m. Central
 * onward. `getFullYear()`/`getMonth()`/`getDate()` read the browser's local calendar instead.
 */
export const todayIsoDate = (now: Date = new Date()): string => {
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Sums calculated staffing cost and scheduled cases through today.
 *
 * A day is "through today" when `day.date <= todayIso`, compared as strings rather than parsed as
 * `Date`s: zero-padded ISO dates sort in calendar order, and `new Date('2026-05-05')` is UTC
 * midnight, which renders as the previous day in every negative-offset timezone.
 *
 * Returns the unrounded totals, or `null` when no day in the week is on or before today.
 */
export const computeCostToDate = (
  days: DailyStaffingDayView[],
  budget: Pick<WeekBudgetView, 'operatingDayLength' | 'costPerHour'>,
  todayIso: string,
): { cost: number; cases: number } | null => {
  const keptDays = days.filter((day) => day.date <= todayIso);
  if (keptDays.length === 0) return null;

  const staffTotal = keptDays.reduce(
    (sum, day) => sum + day.departments.reduce((deptSum, dept) => deptSum + dept.calculatedStaff, 0),
    0,
  );
  const cases = keptDays.reduce((sum, day) => sum + day.scheduledCases, 0);

  return { cost: staffTotal * budget.operatingDayLength * budget.costPerHour, cases };
};
